import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PUBLIC_SETTINGS = {
  site_name: "Frankly Talking English",
  site_tagline: "Speak English with Confidence",
  contact_email: "franklytalkingenglish@gmail.com",
  default_currency: "EUR",
  trial_booking_url: "",
  whatsapp_url: "",
  google_analytics_enabled: false,
  google_analytics_id: "",
  gtm_enabled: false,
  gtm_id: "",
  gtm_auth: "",
  gtm_preview: "",
  recaptcha_site_key: "",
  recaptcha_enabled: false,
  recaptcha_score_threshold: 0.5,
  facebook_url: "",
  instagram_url: "",
  linkedin_url: "",
  youtube_url: "",
  stripe_enabled: false,
  stripe_public_key: "",
  cancellation_window_hours: 24,
};

const PublicSettingsContext = createContext({
  settings: DEFAULT_PUBLIC_SETTINGS,
  loading: true,
  refreshSettings: async () => {},
  executeRecaptcha: async () => null,
});

let recaptchaPromise = null;
let recaptchaSiteKey = null;

function upsertScript(id, config) {
  const existing = document.getElementById(id);
  if (existing) {
    if (config.text !== undefined) {
      existing.text = config.text;
    }
    if (config.src) {
      existing.src = config.src;
    }
    return existing;
  }

  const script = document.createElement("script");
  script.id = id;

  if (config.src) {
    script.src = config.src;
  }

  if (config.async) {
    script.async = true;
  }

  if (config.defer) {
    script.defer = true;
  }

  if (config.text !== undefined) {
    script.text = config.text;
  }

  document.head.appendChild(script);
  return script;
}

function syncDocumentMetadata(settings) {
  const siteName = settings.site_name || DEFAULT_PUBLIC_SETTINGS.site_name;
  const tagline = settings.site_tagline || DEFAULT_PUBLIC_SETTINGS.site_tagline;
  const description = `${siteName} - ${tagline}. Personalized online English lessons.`;

  document.title = siteName;

  const descriptionMeta = document.querySelector('meta[name="description"]');
  if (descriptionMeta) {
    descriptionMeta.setAttribute("content", description);
  }
}

function syncGoogleAnalytics({ enabled, gaId }) {
  if (!enabled || !gaId) {
    return;
  }

  upsertScript(`ga-loader-${gaId}`, {
    src: `https://www.googletagmanager.com/gtag/js?id=${gaId}`,
    async: true,
  });

  upsertScript(`ga-inline-${gaId}`, {
    text: `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      window.gtag = window.gtag || gtag;
      gtag('js', new Date());
      gtag('config', '${gaId}');
    `,
  });
}

function syncGoogleTagManager({ enabled, gtmId, gtmAuth, gtmPreview }) {
  if (!enabled || !gtmId) {
    return;
  }

  const extraParams = [];
  if (gtmAuth) {
    extraParams.push(`gtm_auth=${encodeURIComponent(gtmAuth)}`);
  }
  if (gtmPreview) {
    extraParams.push(`gtm_preview=${encodeURIComponent(gtmPreview)}`);
  }
  if (extraParams.length > 0) {
    extraParams.push("gtm_cookies_win=x");
  }
  const gtmQuerySuffix = extraParams.length > 0 ? `&${extraParams.join("&")}` : "";

  upsertScript(`gtm-inline-${gtmId}`, {
    text: `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),
          dl=l!='dataLayer' ? '&l=' + l : '';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id=' + i + dl + '${gtmQuerySuffix}';
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `,
  });

  if (!document.getElementById(`gtm-noscript-${gtmId}`)) {
    const noscript = document.createElement("noscript");
    noscript.id = `gtm-noscript-${gtmId}`;
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}${gtmQuerySuffix}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.prepend(noscript);
  }
}

function syncAnalytics(settings) {
  syncGoogleAnalytics({
    enabled: settings.google_analytics_enabled,
    gaId: settings.google_analytics_id,
  });
  syncGoogleTagManager({
    enabled: settings.gtm_enabled,
    gtmId: settings.gtm_id,
    gtmAuth: settings.gtm_auth,
    gtmPreview: settings.gtm_preview,
  });
}

function loadRecaptcha(siteKey) {
  if (!siteKey) {
    return Promise.reject(new Error("reCAPTCHA site key is missing"));
  }

  if (recaptchaPromise && recaptchaSiteKey === siteKey && window.grecaptcha) {
    return recaptchaPromise;
  }

  const existingScript = document.getElementById("google-recaptcha");
  if (existingScript && recaptchaSiteKey !== siteKey) {
    existingScript.remove();
  }

  recaptchaSiteKey = siteKey;
  recaptchaPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "google-recaptcha";
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => reject(new Error("Unable to load reCAPTCHA"));
    document.head.appendChild(script);
  });

  return recaptchaPromise;
}

export const usePublicSettings = () => {
  const context = useContext(PublicSettingsContext);
  if (!context) {
    throw new Error("usePublicSettings must be used within a PublicSettingsProvider");
  }
  return context;
};

export const PublicSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_PUBLIC_SETTINGS);
  const [loading, setLoading] = useState(true);

  async function fetchSettings() {
    try {
      const response = await axios.get(`${API_URL}/api/public/settings/public`);
      setSettings((current) => ({ ...current, ...response.data }));
    } catch (error) {
      console.error("Failed to load public settings:", error);
      setSettings(DEFAULT_PUBLIC_SETTINGS);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    syncDocumentMetadata(settings);
    syncAnalytics(settings);
  }, [settings]);

  async function executeRecaptcha(action = "submit") {
    if (!settings.recaptcha_enabled || !settings.recaptcha_site_key) {
      return null;
    }

    const grecaptcha = await loadRecaptcha(settings.recaptcha_site_key);
    await new Promise((resolve) => grecaptcha.ready(resolve));
    return grecaptcha.execute(settings.recaptcha_site_key, { action });
  }

  return (
    <PublicSettingsContext.Provider
      value={{
        settings,
        loading,
        refreshSettings: fetchSettings,
        executeRecaptcha,
      }}
    >
      {children}
    </PublicSettingsContext.Provider>
  );
};

export default PublicSettingsProvider;
